import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

const ThemeIcon = ({ type }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4"
    aria-hidden="true"
  >
    {type === 'light' && (
      <>
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41" />
      </>
    )}
    {type === 'dark' && (
      <path d="M20.5 14.1A8.5 8.5 0 019.9 3.5a8.5 8.5 0 1010.6 10.6z" />
    )}
    {type === 'system' && (
      <>
        <rect x="3" y="4" width="18" height="13" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </>
    )}
  </svg>
);

const THEMES = [
  { value: 'light', labelKey: 'preferences.theme.light', fallback: 'Claro' },
  { value: 'dark', labelKey: 'preferences.theme.dark', fallback: 'Oscuro' },
  {
    value: 'system',
    labelKey: 'preferences.theme.system',
    fallback: 'Sistema',
  },
];

const readPreferences = () => {
  try {
    return JSON.parse(localStorage.getItem('userPreferences')) || {};
  } catch {
    return {};
  }
};

export default function AccountThemeSwitcher() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState(() => {
    const stored = readPreferences().theme;
    return THEMES.some(({ value }) => value === stored) ? stored : 'system';
  });

  const selectTheme = (nextTheme) => {
    const preferences = { ...readPreferences(), theme: nextTheme };
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    window.__applyTheme?.(nextTheme);
    setTheme(nextTheme);
  };

  const handleKeyDown = (event, index) => {
    const targetIndex = {
      ArrowLeft: (index + THEMES.length - 1) % THEMES.length,
      ArrowUp: (index + THEMES.length - 1) % THEMES.length,
      ArrowRight: (index + 1) % THEMES.length,
      ArrowDown: (index + 1) % THEMES.length,
      Home: 0,
      End: THEMES.length - 1,
    }[event.key];
    if (targetIndex === undefined) return;
    event.preventDefault();
    selectTheme(THEMES[targetIndex].value);
    event.currentTarget.parentElement.querySelectorAll('[role="radio"]')[targetIndex]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('preferences.theme.title', 'Apariencia')}
      className="mt-1 flex items-center gap-1 border-t border-[var(--rely-border-subtle)] px-2 pt-2"
    >
      {THEMES.map(({ value, labelKey, fallback }, index) => {
        const label = t(labelKey, fallback);
        const selected = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            aria-label={label}
            title={label}
            onClick={() => selectTheme(value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'flex h-10 flex-1 items-center justify-center rounded-[var(--rely-radius-control)] border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rely-color-brand-green)]',
              selected
                ? 'border-[var(--rely-color-brand-green)] bg-[var(--rely-surface-selected)] text-[var(--rely-text-strong)]'
                : 'border-transparent text-[var(--rely-text-muted)] hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)]'
            )}
          >
            <ThemeIcon type={value} />
          </button>
        );
      })}
    </div>
  );
}
